<template lang="pug">
  div.p-select(translate="no" :class="containerClass" :style="containerStyle")
    multiselect(
      v-bind="multiselectAttrs"
      v-model="parsedState"
      :options="customOptions"
      :placeholder="forcedPlaceholder || title"
      :forcedLabelUp="Boolean(forcedPlaceholder)"
      ref="multiselect"
      :optionsLimit="limit"
      :track-by="trackBy"
      :label="labelItem"
      :loading="objectLoading || commonLoading"
      :openDirection="openDirection"
      :closeOnSelect="true"
      :allowEmpty="allowEmpty"
      :disabled="disabled"
      :required="required"
      :error="Boolean(error)"
      @select="(option, id) => $emit('select', option, id)"
      @search-change="changeSearch"
      @load-more="carregarMaisPorScroll"
      @open="$emit('open')"
      @close="$emit('close')"
      id="multiselect"
    )
      template(#logoIcon)
        slot(name="logoIcon")

      template(v-if="mostrarBotaoCarregarMais" #opcaoEspecial)
        PontotelBotao(peso="pesado"
          @clique="fetchItemsComPaginacao"
          tipo="primario"
          tamanho="P"
        ) {{ $t('compartilhados.selectors.PSelect.carregarMais') }}

      template(#selection="{ values, search, isOpen }")
        template(v-if="!isOpen")
          span.multiselect__single(v-if="values.length > 1")
            | {{ values.length }} {{ $t('compartilhados.selectors.PSelect.itensSelecionados') }}

          span.multiselect__single(v-else-if="values.length === 1")
            | {{ values[0][labelItem] }}

      template(#tag="{ option, remove }")
        span.custom__tag(style="display: none;")

      template(#noResult)
        span.multiselect__no-result
          | {{ $t('compartilhados.selectors.PSelect.nadaEncontrado') }}

      template(#noOptions)
        span.multiselect__no-options
          | {{ $t('compartilhados.selectors.PSelect.nenhumaOpcao') }}

      template(#option="props")
        span.multiselect__option-label(
          :title="props.option[labelItem]",
          :aria-label="props.option[labelItem]",
          :data-marquee-text="props.option[labelItem]"
        )
          | {{ props.option[labelItem] }}

    ErrorMsg(v-if="error || !mensagemDeInformacao" :error="error")
    InfoMsg(v-if="mensagemDeInformacao" :info="mensagemDeInformacao")

</template>

<script>
import canUseCommonState from 'src/common/mixins/canUseCommonState'
import Multiselect from './_MultiselectWrapperLegado'
import fetchItems from 'src/common/mixins/fetchItems'
import ErrorMsg from 'src/common/inputs/ErrorMsg'
import InfoMsg from '@/common/inputs/InfoMsg.vue'
import { includesSearch } from 'src/utils'
import { debounce } from 'lodash'
import { extrairAtributosDeControle, extrairClasseDoContainer, extrairEstiloDoContainer } from 'src/common/mixins/localListenersAndAttributes'

export default {
  name: 'PSelectLegado',
  inheritAttrs: false,
  emits: ['update:modelValue', 'change', 'select', 'open', 'close', 'asyncSearch'],
  mixins: [canUseCommonState(false), fetchItems()],
  components: {
    Multiselect, ErrorMsg, InfoMsg
  },
  props: {
    usaPaginacao: Boolean,
    disabled: Boolean,
    tamanhoMaximoCaracteresInseridos: {
      type: Number,
      'default': 200
    },
    labelItem: { type: String, required: true },
    title: { type: String },
    forcedPlaceholder: { type: String, default: '' },
    trackBy: { type: String, 'default': 'id' },
    customLabel: { type: Function },
    noSelectedOption: {type: String}, // show a option in the begin for not selected
    noSelectedOptionValue: { default: null },
    loading: { type: Boolean },
    required: { type: Boolean, 'default': false },
    allowEmpty: { type: Boolean, default: true },
    limit: { type: Number, 'default': -1 },
    openDirection: {type: String, 'default': 'below'},
    options: {type: Array, 'default': null},
    // if options is giver this component will not use the items from loaded
    // its usefull to make this component work like:
    //            input(type="select" v-model="state")
    //              option a
    //              option b
    error: String,
    sortAlphabetically: { type: Boolean, 'default': false },
    loadDataFunction: { type: Function },
    mensagemDeInformacao: { type: String, default: '' }
  },
  data () {
    return {
      search: ''
    }
  },
  created () {
    this.debouncedSearch = debounce((query) => {
      this.search = query
      // Busca no servidor só enquanto a lista for paginada e ainda NÃO estiver
      // completa em memória; se já está completa (pré-carga/cache), o customOptions
      // filtra no cliente e não precisamos ir à rede (nem do "carregar mais").
      if (this.usaPaginacao && !this.moduloTemListaCompleta()) {
        this.fetchItemsComPaginacao(false)
      }
    }, 150)
  },
  beforeUnmount () {
    this.debouncedSearch?.cancel()
  },
  watch: {
    loadDataFunction: {
      immediate: true,
      handler (loadDataFunction) {
        if (loadDataFunction) { this.loadDataFunction() }
      }
    }
  },
  computed: {
    containerClass () {
      return extrairClasseDoContainer(this.$attrs)
    },
    containerStyle () {
      return extrairEstiloDoContainer(this.$attrs)
    },
    multiselectAttrs () {
      return extrairAtributosDeControle(this.$attrs)
    },
    aindaFaltaMostrarPaginas () {
      let { useCommon } = this
      if (useCommon.includes('/')) {
        useCommon = useCommon.split('/')[0]
      }
      return this.$store.getters[`common/${useCommon}/aindaFaltaMostrarPaginas`]
    },
    mostrarBotaoCarregarMais () {
      const {usaPaginacao} = this
      if (!usaPaginacao) return
      return this.aindaFaltaMostrarPaginas || false
    },
    customOptions: {
      get () {
        let ops = this.options ? this.options : this.state.loaded
        if (!ops) return []

        ops = ops.filter(option => {
          if (!option[this.labelItem]) return false
          return includesSearch(option[this.labelItem], this.search)
        })

        if (this.sortAlphabetically) {
          ops = ops.sort((a, b) => {
            const key = this.labelItem
            if (a[key] < b[key]) return -1
            if (a[key] > b[key]) return 1
            return 0
          })
        }

        if (this.noSelectedOption) {
          ops = [{[this.labelItem]: this.noSelectedOption, id: this.noSelectedOptionValue}, ...ops]
        }
        return ops
      }
    },
    parsedState: {
      set (value) {
        // Ao dar duplo click, por algum motivo esse value chega como array, e não é isso que queremos aqui.
        if (Array.isArray(value)) {
          value = value[0]
        }
        if (this.options) {
          if (!value) {
            this.state = null
          } else {
            this.state = value[this.trackBy]
          }
        } else {
          this.state = { ...this.state, selected: value }
        }
      },
      get () {
        if (this.options) {
          return this.options.find(i => i[this.trackBy] === this.state)
        } else {
          return this.state.selected
        }
      }
    },
    objectLoading: {
      get () {
        return !this.options && this.state.loading
      }
    }
  },
  methods: {
    async fetchItemsComPaginacao (append = true) {
      let {useCommon, search, withFields, withMinimalInformation, withParams} = this

      if (useCommon.includes('/')) {
        useCommon = useCommon.split('/')[0]
      }
      this.$store.dispatch(`common/${useCommon}/fetchItemsComPaginacao`, { append, search, withFields, withMinimalInformation, withParams })
    },
    carregarMaisPorScroll () {
      // Auto-carrega a próxima página ao rolar até perto do fim (substitui o
      // clique em "Carregar mais"). Guardas: só paginado, só se ainda há páginas
      // e se não há fetch em voo (evita disparos repetidos do scroll). O append
      // preserva a busca atual, então funciona também com filtro por nome.
      if (!this.usaPaginacao || !this.useCommon) { return }
      if (!this.aindaFaltaMostrarPaginas) { return }
      const modulo = this.useCommon.split('/')[0]
      if (this.$store.state.common[modulo]?.loading) { return }
      this.fetchItemsComPaginacao(true)
    },
    open () {
      this.$refs.multiselect.activate()
    },
    focus () {
      return this.open()
    },
    verificaTamanhoCaracteres () {
      const motivo = this.$refs.multiselect.getSearch()
      if (motivo.length > this.tamanhoMaximoCaracteresInseridos) {
        this.$refs.multiselect.setSearch(motivo.substring(0, this.tamanhoMaximoCaracteresInseridos))
      }
    },
    changeSearch (query) {
      const querySemEspacos = query.trim()

      this.$emit('asyncSearch', query)
      this.verificaTamanhoCaracteres()
      this.debouncedSearch(querySemEspacos)
    },
    limparCampoDePesquisa () {
      this.$refs.multiselect.clearSearch()
    }
  }
}
</script>

<style>
.p-select__error--message {
  display: block;
  font-size: .75rem;
  padding-top: 2px;
  padding-bottom: 2px;
  color: var(--color-danger);
}

.campoBloqueadoSankhya input:disabled + .p-textfield__title .p-textfield__title-text {
  color: var(--input-cor-desabilitado-conteudo);
}

/* Logo label style */
.p-textfield__logoicon {
  margin: 0px 3px 0px 5px;
}
</style>
